import { AxiosError } from 'axios';
import { SubscriptionError } from '@/types/subscription';

type ErrorBody = Record<string, any>;

function bodyOf(error: any): ErrorBody | null {
  const data = error?.response?.data;
  return data && typeof data === 'object' ? data : null;
}

/**
 * The kind of a billing error. The server sends `code` at the top and `errors.type` inside
 * `errors`; the older Rails body had `type` at the top. `errors` may also be a plain list of messages.
 */
function kindOf(data: ErrorBody | null): string | undefined {
  if (!data) return undefined;
  const nested = data.errors && !Array.isArray(data.errors) ? data.errors.type : undefined;
  return data.code ?? nested ?? data.type;
}

/** The fields of the error: inside `errors` for the Rust server, at the top for the Rails body. */
function detailsOf(data: ErrorBody): ErrorBody {
  return data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors) ? data.errors : data;
}

/**
 * The server names the next plan in its message ("Upgrade to Crew to add more.") and sends its code in
 * `recommended_plan`. The screen shows the name, so read it from the message and fall back to the code.
 */
function planNameOf(details: ErrorBody): string | undefined {
  const named = /Upgrade to (.+?) to add more/.exec(details.message ?? '')?.[1];
  return named ?? titleCase(details.recommended_plan);
}

function titleCase(code: unknown): string | undefined {
  return typeof code === 'string' && code ? code.charAt(0).toUpperCase() + code.slice(1) : undefined;
}

/**
 * Check if an error is a subscription-related error
 */
export function isSubscriptionError(error: any): boolean {
  const kind = kindOf(bodyOf(error));
  return kind === 'subscription_limit' || kind === 'feature_disabled';
}

/** A 403 `subscription_required`: the organization is read-only, so the write was refused. */
export function isSubscriptionRequired(error: any): boolean {
  return error?.response?.status === 403 && kindOf(bodyOf(error)) === 'subscription_required';
}

/** A 403 `impersonation_forbidden`: the action (change e-mail or password) is refused while a platform admin is signed in as the user. */
export function isImpersonationForbidden(error: any): boolean {
  return error?.response?.status === 403 && kindOf(bodyOf(error)) === 'impersonation_forbidden';
}

/** A 422 `too_many_teammates`: the team is bigger than the plan being bought. */
export function isTooManyTeammates(error: any): boolean {
  return kindOf(bodyOf(error)) === 'too_many_teammates';
}

/** A 403 `organization_unavailable`: the invitation's organization cannot take members. The invitee is told neutrally. */
export function isOrganizationUnavailable(error: any): boolean {
  return error?.response?.status === 403 && kindOf(bodyOf(error)) === 'organization_unavailable';
}

/** The reason of a `subscription_required` refusal: `trial_ended`, `payment_lapsed` or `cancelled`. */
export function subscriptionRequiredReason(error: any): string | undefined {
  const data = bodyOf(error);
  return data && isSubscriptionRequired(error) ? detailsOf(data).reason : undefined;
}

/** The refusal of a `subscription_required` error: its reason and the server's message. */
export function subscriptionRequiredRefusal(error: any): { reason?: string; message?: string } | null {
  const data = bodyOf(error);
  if (!data || !isSubscriptionRequired(error)) return null;
  const details = detailsOf(data);
  return {
    reason: typeof details.reason === 'string' ? details.reason : undefined,
    message: typeof details.message === 'string' ? details.message : undefined,
  };
}

/**
 * Extract subscription error details from an Axios error
 */
export function extractSubscriptionError(error: AxiosError): SubscriptionError | null {
  const data = bodyOf(error);
  const kind = kindOf(data);

  if (!data || (kind !== 'subscription_limit' && kind !== 'feature_disabled')) {
    return null;
  }

  const details = detailsOf(data);

  return {
    type: kind,
    limit: details.limit,
    limitName: details.limit_name,
    current: details.current,
    max: details.max,
    feature: details.feature,
    featureName: details.feature_name,
    description: details.description,
    currentPlan: titleCase(details.current_plan),
    requiredPlan: details.required_plan,
    recommendedPlan: planNameOf(details),
    message: details.message,
    upgradeUrl: details.upgrade_url,
  };
}
